import { useQueryClient, useQuery } from '@tanstack/react-query';
import {
  useMinIoFileStorageServiceGetApiFilesMyFiles,
  useMinIoFileStorageServiceGetApiFilesByRelatedObjectByIdByType,
} from '../../api/generated/queries/queries';
import { FileMetadataDTO } from '../../api/generated/requests/types.gen';
import { MinIoFileStorageService } from '../../api/generated/requests/services.gen';

/**
 * Hook for file read operations (queries) extracted from useFileData
 * Separates read operations from write operations as mentioned in the refactoring requirements
 */
export const useFileQuery = (filters?: { status?: string }) => {
  const queryClient = useQueryClient();
  const queryKey = ['files', filters];

  // Get all user files with custom configuration as specified in the issue
  const { data: files = [], isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () => MinIoFileStorageService.getApiFilesMyFiles(),
    staleTime: 0, // Always treat data as stale
    refetchOnWindowFocus: true, // Refetch when returning to window
    refetchOnMount: true, // Refetch when component mounts
    refetchInterval: 60000, // Refetch every minute
  });

  const refreshFiles = refetch;

  // Cache management functions
  const updateFileInCache = (fileId: string, updates: Partial<FileMetadataDTO>) => {
    queryClient.setQueryData(
      ['files'],
      (oldData: FileMetadataDTO[] | undefined) => {
        if (!oldData) return oldData;
        return oldData.map(file => 
          file.filename === fileId ? { ...file, ...updates } : file
        );
      }
    );
  };

  const addFileToCache = (newFile: FileMetadataDTO) => {
    queryClient.setQueryData(
      ['files'],
      (oldData: FileMetadataDTO[] | undefined) => {
        return oldData ? [newFile, ...oldData] : [newFile];
      }
    );
  };

  const removeFileFromCache = (filename: string) => {
    queryClient.setQueryData(
      ['files'],
      (oldData: FileMetadataDTO[] | undefined) => {
        if (!oldData) return oldData;
        return oldData.filter(file => file.filename !== filename);
      }
    );
  };

  // Filter functions
  const getFilesByRelatedObject = (relatedObjectId: number, relatedObjectType: string) => {
    return files.filter(file => 
      file.relatedObjectId === relatedObjectId.toString() && 
      file.relatedObjectType === relatedObjectType
    );
  };

  const getFilesByStatus = (status: 'ACTIVE' | 'DETACHED') => {
    return files.filter(file => 
      status === 'ACTIVE' ? (file.status === 'ACTIVE' || !file.status) : file.status === status
    );
  };

  return {
    // Data
    files,
    isLoading,
    error,
    
    // Actions
    refreshFiles,
    
    // Cache management
    updateFileInCache,
    addFileToCache,
    removeFileFromCache,
    
    // Filters
    getFilesByRelatedObject,
    getFilesByStatus,
  };
};

/**
 * Hook for getting files by related object (extracted from useFileData)
 */
export const useRelatedFilesQuery = (relatedObjectId: number, relatedObjectType: string) => {
  const { 
    data: files = [], 
    isLoading, 
    error 
  } = useMinIoFileStorageServiceGetApiFilesByRelatedObjectByIdByType({
    id: relatedObjectId.toString(),
    type: relatedObjectType
  });

  return {
    files,
    isLoading,
    error
  };
};